<script setup lang="ts">
import { computed, inject } from 'vue'

import {
  getEffectiveCreatorClickLimit,
  getEffectiveCreatorTrafficSourceCosts,
  isCreatorClickLimitOverrideRemoved,
} from '../../../adapters/campaign-creator.adapter'
import { formatterProviderKey } from '../../../constants'
import { defaultFormatter, formatNumber } from '../../../formatters'
import type { CampaignDetailsCreator, CampaignOverviewViewMode } from '../../../types/campaign.types'
import type { TrafficSourceCosts } from '../../../types/traffic-source-costs.types'
import { round } from '../../../utils'
import BoxComponent from '../../atoms/BoxComponent.vue'
import FlexComponent from '../../atoms/FlexComponent.vue'
import ProfileImageComponent from '../../atoms/ProfileImageComponent.vue'
import ProgressComponent from '../../atoms/ProgressComponent.vue'
import TooltipComponent from '../../atoms/TooltipComponent.vue'
import TypographyComponent from '../../atoms/TypographyComponent.vue'

const props = defineProps<{
  creator: CampaignDetailsCreator
  isActive: boolean
  currency: string
  maxClicks?: number
  trafficSourceCosts?: TrafficSourceCosts[]
  viewMode: CampaignOverviewViewMode
}>()
defineSlots<{
  'creator-name'?(props: { creator: CampaignDetailsCreator }): void
  'clicks-action'?(props: { creator: CampaignDetailsCreator }): void
  'costs-action'?(props: { creator: CampaignDetailsCreator }): void
  'costs-breakdown'?(props: { costs: TrafficSourceCosts[]; currency: string }): void
}>()

const { formatMoney: formatMoneyForContext } = inject(formatterProviderKey, defaultFormatter)
const formatMoney = (amount: number, notation: 'compact' | 'standard') =>
  formatMoneyForContext(
    { amount, currency: props.currency },
    { notation, minimumFractionDigits: 0, maximumFractionDigits: 2, allowZero: true },
  )
const isMoneyTooltipDisabled = (amount: number) => !amount || formatMoney(amount, 'compact') === formatMoney(amount, 'standard')
const userName = computed(() => props.creator.displayName || '')
const hasOverrideNoClickLimit = computed(() => isCreatorClickLimitOverrideRemoved(props.creator))
const creatorClickLimit = computed(() => getEffectiveCreatorClickLimit(props.creator, props.maxClicks))

const creatorCosts = computed(() => getEffectiveCreatorTrafficSourceCosts(props.creator, props.trafficSourceCosts))
const isCostsOverrideEnabled = computed(() => !props.creator.invitePending)
const showCosts = computed(() => !!creatorCosts.value.length && isCostsOverrideEnabled.value)
const showCreatorCosts = computed(() => !!props.creator.overrides?.trafficSourcesCosts?.length && isCostsOverrideEnabled.value)
</script>

<template>
  <BoxComponent
    :p="16"
    :class="[
      'creator',
      'override-click-limit',
      {
        inactive: !isActive && showCosts,
        'invite-pending': creator.invitePending,
        'override-traffic-source-costs': showCosts,
        'individual-traffic-source-costs': showCreatorCosts,
      },
    ]"
  >
    <FlexComponent align="center" :gap="24" justify="space-between">
      <FlexComponent is-child flex="36 1 0%">
        <FlexComponent justify="space-between" align="center">
          <FlexComponent align="center" :gap="8" class="creator__info">
            <div v-if="showCosts" class="creator__override-costs-button-wrapper">
              <slot name="costs-action" :creator="creator" />
            </div>

            <FlexComponent is-child flex="0" class="creator__avatar-container">
              <ProfileImageComponent :image="creator.avatar" :name="userName" :alt="userName" :size="32" />
            </FlexComponent>
            <div class="creator__container">
              <div class="creator__info__username-link">
                <slot name="creator-name" :creator="creator">
                  <TypographyComponent variant="title-medium" class="creator__info__username" text-truncate>
                    {{ userName }}
                  </TypographyComponent>
                </slot>
              </div>
              <div v-if="showCosts" class="creator__costs">
                <slot name="costs-breakdown" :costs="creatorCosts" :currency="currency" />
              </div>
            </div>
          </FlexComponent>
          <TypographyComponent v-if="!isActive && creator.invitePending" variant="body-small" color="var(--secondary-64)">
            Invited, waiting for acceptance
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
      <FlexComponent v-if="!creator.invitePending" is-child flex="8.3 1 0%">
        <TooltipComponent :disabled="!creator.contentCount" placement="bottom-end" effect="light" :show-arrow="false">
          <TypographyComponent is="p" variant="body-small" text-align="right">
            {{ formatNumber(creator.contentCount, 'compact') }}
          </TypographyComponent>
          <template #content>
            {{ formatNumber(creator.contentCount) }}
          </template>
        </TooltipComponent>
      </FlexComponent>
      <FlexComponent v-if="!creator.invitePending" is-child flex="11.5 1 0%" class="creator__override-clicks-wrapper">
        <TooltipComponent
          v-if="hasOverrideNoClickLimit"
          :disabled="!creator.clicksCount"
          placement="bottom-end"
          effect="light"
          :show-arrow="false"
        >
          <TypographyComponent is="p" variant="body-small" text-align="right">
            {{ formatNumber(creator.clicksCount, 'compact') }}
          </TypographyComponent>
          <template #content>
            {{ formatNumber(creator.clicksCount) }}
          </template>
        </TooltipComponent>
        <ProgressComponent
          v-else-if="!!creatorClickLimit"
          :content="`${formatNumber(creator.clicksCount, 'compact')}/${formatNumber(creatorClickLimit, 'compact')}`"
          :tooltip-content="`${formatNumber(creator.clicksCount)}/${formatNumber(creatorClickLimit)}`"
          :percentage="Math.ceil((creator.clicksCount / creatorClickLimit) * 100)"
        />
        <TooltipComponent v-else :disabled="!creator.clicksCount" placement="bottom-end" effect="light" :show-arrow="false">
          <TypographyComponent is="p" variant="body-small" text-align="right">
            {{ formatNumber(creator.clicksCount, 'compact') }}
          </TypographyComponent>
          <template #content>
            {{ formatNumber(creator.clicksCount) }}
          </template>
        </TooltipComponent>
        <div class="creator__override-clicks-button">
          <slot name="clicks-action" :creator="creator" />
        </div>
      </FlexComponent>
      <FlexComponent v-if="!creator.invitePending" is-child flex="10 1 0%">
        <TooltipComponent
          :disabled="isMoneyTooltipDisabled(creator.estimatedMediaValue)"
          placement="bottom-end"
          effect="light"
          :show-arrow="false"
        >
          <TypographyComponent is="p" variant="body-small" text-align="right">
            {{ formatMoney(creator.estimatedMediaValue, 'compact') }}
          </TypographyComponent>
          <template #content>
            {{ formatMoney(creator.estimatedMediaValue, 'standard') }}
          </template>
        </TooltipComponent>
      </FlexComponent>
      <FlexComponent v-if="!creator.invitePending && viewMode === 'admin'" is-child flex="11.4 1 0%">
        <TypographyComponent is="p" variant="body-small" text-align="right">
          {{ round(creator.gpRatio, 1) }}
        </TypographyComponent>
      </FlexComponent>
      <FlexComponent v-if="!creator.invitePending && viewMode === 'admin'" is-child flex="12 1 0%">
        <TooltipComponent :disabled="isMoneyTooltipDisabled(creator.estimatedGp)" placement="bottom-end" effect="light" :show-arrow="false">
          <TypographyComponent is="p" variant="body-small" text-align="right">
            {{ formatMoney(creator.estimatedGp, 'compact') }}
          </TypographyComponent>
          <template #content>
            {{ formatMoney(creator.estimatedGp, 'standard') }}
          </template>
        </TooltipComponent>
      </FlexComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
.creator {
  border-radius: 16px;
  border: 1px solid var(--secondary-8);

  &.invite-pending {
    background-color: var(--item-hover);
  }

  &.inactive:hover {
    background-color: var(--item-hover);
  }

  &.individual-traffic-source-costs {
    .creator__info__username-link,
    .creator__info__username,
    .creator__costs {
      transform: translateY(0);
    }

    .creator__override-costs-button-wrapper :deep(button) {
      transform: translateY(0);
    }
  }

  &.override-traffic-source-costs:hover {
    .creator__info__username-link,
    .creator__info__username,
    .creator__costs {
      transform: translateY(0);
    }

    .creator__costs {
      overflow: auto;
    }

    .creator__override-costs-button-wrapper :deep(button) {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.override-click-limit:hover {
    .creator__override-clicks-button :deep(button) {
      opacity: 1;
    }
  }
}

.creator__info {
  display: inline-flex;
  position: relative;
  max-width: 100%;
}

.creator__avatar-container {
  width: 32px;
  height: 36px;
  display: flex;
  align-items: center;
}

.creator__info__tag {
  width: 32px;
  height: 32px;
  border-radius: 32px;
}

.creator__container {
  width: 100%;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  height: 36px;
}

.creator__info__username-link,
.creator__info__username,
.creator__costs {
  min-width: 0;
  transition: all 0.4s ease-in-out;
}

.creator__info__username-link {
  min-height: 20px;
  display: block;
  transform: translateY(8px);
  color: inherit;
  text-decoration: none;
}

.creator__info__username {
  display: block;
}

.creator__costs {
  display: inline-flex;
  position: relative;
  transform: translateY(16px);
  overflow: visible;
}

.creator__override-costs-button-wrapper {
  position: absolute;
  height: 36px;
  left: calc(100% + 4px);
  bottom: 0;
  display: flex;
  align-items: flex-end;
  overflow: hidden;

  :deep(button) {
    opacity: 0;
    transform: translateY(16px);
    transition: all 0.3s ease-in-out;
  }
}

.creator__override-clicks-wrapper {
  position: relative;
}

.creator__override-clicks-button {
  position: absolute;
  left: calc(100% + 4px);
  top: 0;
  z-index: 10;

  :deep(button) {
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }
}

:deep(.icon) {
  path {
    fill: currentColor;
  }
}
</style>
